import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '../ui';
import { Mail, Eye, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface EmailSettings {
  enabled: boolean;
  relance_devis_enabled: boolean;
  relance_devis_jours: number;
  rappel_j7_enabled: boolean;
  rappel_j1_enabled: boolean;
  acompte_auto_enabled: boolean;
}

const DEFAULTS: EmailSettings = {
  enabled: false,
  relance_devis_enabled: true,
  relance_devis_jours: 7,
  rappel_j7_enabled: true,
  rappel_j1_enabled: true,
  acompte_auto_enabled: true,
};

const TEMPLATES: { key: string; label: string; description: string }[] = [
  { key: 'relance-devis', label: 'Relance devis', description: 'Relance automatique X jours après envoi du devis' },
  { key: 'confirmation-acompte', label: 'Confirmation acompte', description: 'Envoyée automatiquement à réception d\'un acompte' },
  { key: 'rappel-evenement', label: 'Rappel événement (J-7 / J-1)', description: 'Rappels logistiques avant l\'événement' },
];

export function EmailsAutoCard() {
  const [settings, setSettings] = useState<EmailSettings>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from('email_settings')
        .select('enabled, relance_devis_enabled, relance_devis_jours, rappel_j7_enabled, rappel_j1_enabled, acompte_auto_enabled')
        .eq('id', 1)
        .maybeSingle();
      if (!error && data) setSettings({ ...DEFAULTS, ...data });
      setLoading(false);
    })();
  }, []);

  const update = async (patch: Partial<EmailSettings>) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    setSaving(true);
    const { error } = await supabase.from('email_settings').update(patch).eq('id', 1);
    setSaving(false);
    if (error) toast.error('Erreur de sauvegarde');
    else toast.success('Préférences enregistrées');
  };

  const preview = async (templateName: string) => {
    setPreviewing(templateName);
    try {
      const { data, error } = await supabase.functions.invoke('preview-transactional-email', {
        body: { templateName },
      });
      if (error) throw error;
      const html = (data as any)?.html;
      if (!html) throw new Error('Aperçu indisponible');
      const w = window.open('', '_blank', 'width=720,height=900');
      if (w) { w.document.write(html); w.document.close(); }
      else toast.error('Pop-up bloqué');
    } catch (e: any) {
      toast.error(`Aperçu impossible : ${e?.message ?? 'erreur'}`);
    } finally {
      setPreviewing(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-5 mb-6">
        <div className="text-xs text-slate-400 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" strokeWidth={1.5} /> Chargement…
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-5 mb-6 border-violet-500/30 bg-violet-500/5">
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-violet-300">
          <Mail size={14} strokeWidth={1.5} /> Emails automatiques
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <span className={`text-xs font-medium ${settings.enabled ? 'text-emerald-300' : 'text-slate-400'}`}>
            {settings.enabled ? 'Envoi ACTIVÉ' : 'Envoi désactivé (mode aperçu)'}
          </span>
          <span className={`relative inline-block w-11 h-6 rounded-full transition-colors ${settings.enabled ? 'bg-emerald-500' : 'bg-slate-700'}`}>
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => update({ enabled: e.target.checked })}
              disabled={saving}
              className="sr-only"
            />
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${settings.enabled ? 'translate-x-5' : ''}`} />
          </span>
        </label>
      </div>

      {!settings.enabled && (
        <div className="flex items-start gap-2 mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-md text-[11px] text-amber-200">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" strokeWidth={1.5} />
          <span>
            <strong>Mode aperçu uniquement.</strong> Les déclencheurs sont câblés mais aucun email ne sera envoyé tant que le commutateur principal est désactivé.
            Vous pouvez prévisualiser le rendu de chaque modèle ci-dessous.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div className="space-y-3">
          <SwitchRow
            label="Relance devis non répondu"
            checked={settings.relance_devis_enabled}
            onChange={(v) => update({ relance_devis_enabled: v })}
            disabled={saving}
          >
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[11px] text-slate-400">Délai :</span>
              <input
                type="number"
                min={1}
                max={60}
                value={settings.relance_devis_jours}
                onChange={(e) => update({ relance_devis_jours: Math.max(1, Number(e.target.value) || 7) })}
                disabled={saving || !settings.relance_devis_enabled}
                className="w-16 px-2 py-1 bg-slate-950/60 border border-slate-800 rounded-md text-xs text-slate-200 focus:outline-none focus:border-violet-500/50 disabled:opacity-50"
              />
              <span className="text-[11px] text-slate-400">jours après envoi</span>
            </div>
          </SwitchRow>
          <SwitchRow
            label="Confirmation d'acompte (auto)"
            checked={settings.acompte_auto_enabled}
            onChange={(v) => update({ acompte_auto_enabled: v })}
            disabled={saving}
          />
        </div>
        <div className="space-y-3">
          <SwitchRow
            label="Rappel J-7 avant événement"
            checked={settings.rappel_j7_enabled}
            onChange={(v) => update({ rappel_j7_enabled: v })}
            disabled={saving}
          />
          <SwitchRow
            label="Rappel J-1 avant événement"
            checked={settings.rappel_j1_enabled}
            onChange={(v) => update({ rappel_j1_enabled: v })}
            disabled={saving}
          />
        </div>
      </div>

      <div className="border-t border-violet-500/20 pt-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Aperçu des modèles</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {TEMPLATES.map((t) => (
            <button
              key={t.key}
              onClick={() => preview(t.key)}
              disabled={previewing === t.key}
              className="flex items-center gap-2 px-3 py-2.5 bg-slate-950/50 hover:bg-slate-950/80 border border-slate-800 hover:border-violet-500/40 rounded-md text-left transition-colors group disabled:opacity-50"
            >
              {previewing === t.key
                ? <Loader2 size={14} className="animate-spin text-violet-400 shrink-0" strokeWidth={1.5} />
                : <Eye size={14} className="text-violet-400 shrink-0 group-hover:text-violet-300" strokeWidth={1.5} />
              }
              <div className="min-w-0">
                <div className="text-xs text-slate-200 font-medium truncate">{t.label}</div>
                <div className="text-[10px] text-slate-500 truncate">{t.description}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </Card>
  );
}

function SwitchRow({
  label, checked, onChange, disabled, children,
}: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; children?: React.ReactNode }) {
  return (
    <div className="bg-slate-950/40 border border-slate-800 rounded-md p-3">
      <label className="flex items-center justify-between gap-3 cursor-pointer">
        <span className="text-xs text-slate-200">{label}</span>
        <span className={`relative inline-block w-9 h-5 rounded-full transition-colors ${checked ? 'bg-violet-500' : 'bg-slate-700'}`}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            className="sr-only"
          />
          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-4' : ''}`} />
        </span>
      </label>
      {children}
    </div>
  );
}
